import React from 'react';
import {
  Typography,
  Button,
  Grid,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import { SEOHead } from '../../components/SEO';
import {
  CheckCircle,
  AccountTree,
  Search,
  AutoAwesome,
  Storage,
  ArrowForward,
  ExpandMore,
  Description,
  Email,
  Psychology,
  Speed,
  Security,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const KnowledgeManagementPage: React.FC = () => {
  const navigate = useNavigate();

  // FAQ data based on AI knowledge management research
  const faqData = [
    {
      question: "What is an AI knowledge management system?",
      answer: "An AI knowledge management system is a centralized repository that uses artificial intelligence to capture, organize, and make organizational knowledge discoverable. It leverages natural language processing and machine learning to analyze vast amounts of data, create semantic connections, and provide intelligent insights from your existing information sources."
    },
    {
      question: "How does Prismatic Forge's knowledge management differ from traditional systems?",
      answer: "Unlike traditional knowledge bases that require manual organization, Prismatic Forge automatically creates semantic connections between your emails, documents, and conversations. Sam (our document processing teammate) continuously learns from your organization's information patterns and surfaces relevant insights without requiring manual tagging or categorization."
    },
    {
      question: "Can AI knowledge management systems handle unstructured data?",
      answer: "Yes, AI excels at managing unstructured data like emails, chat transcripts, meeting notes, and documents. Prismatic Forge processes these diverse formats automatically, extracting key information and creating meaningful relationships between different data sources to provide comprehensive organizational intelligence."
    },
    {
      question: "How secure is my organizational knowledge in an AI system?",
      answer: "Prismatic Forge implements enterprise-grade security with end-to-end encryption, role-based access controls, and compliance with major frameworks including ISO 27001, SOC 2, and GDPR. Your knowledge graph maintains strict access controls ensuring only authorized users can access specific information."
    },
    {
      question: "What integrations are available for knowledge management?",
      answer: "Prismatic Forge integrates with Microsoft Exchange, Gmail, Google Workspace, Microsoft Graph, OneDrive, SharePoint, and Google Drive. Email-only integrations are available across all subscription tiers, while full workplace integrations require Professional plans or higher."
    },
    {
      question: "How does AI knowledge management improve research and decision making?",
      answer: "AI knowledge management accelerates research by 80% through intelligent synthesis of information from all your sources. It automatically identifies patterns, surfaces non-obvious connections, and provides comprehensive analysis with citations, enabling faster and more informed decision-making across your organization."
    },
    {
      question: "Will AI replace knowledge managers and information professionals?",
      answer: "No, AI enhances rather than replaces knowledge professionals. By automating repetitive tasks like categorization and search, AI allows knowledge managers to focus on strategic activities like knowledge strategy, relationship building, and insight interpretation that require human expertise and judgment."
    },
    {
      question: "How long does it take to see value from an AI knowledge management system?",
      answer: "With Prismatic Forge, you can see immediate value as Sam begins analyzing your email and document history from day one. Most organizations experience significant productivity improvements within the first week, with deeper insights and knowledge connections developing over the first 30 days."
    }
  ];

  const samExamples = [
    {
      task: "Document Analysis",
      input: "Upload quarterly financial reports",
      output: "Extracts key metrics, trends, and insights. Creates semantic connections to related documents, past quarters, and relevant business context.",
      insights: "Revenue growth patterns, cost structure changes, market position shifts"
    },
    {
      task: "Email Intelligence",
      input: "Search: 'project timeline discussions'",
      output: "Finds relevant email threads, extracts project milestones, identifies stakeholders, and maps communication patterns.",
      insights: "Project dependencies, stakeholder concerns, timeline risks"
    },
    {
      task: "Knowledge Synthesis",
      input: "Research request on market trends",
      output: "Combines internal documents, external research, and email context to create comprehensive analysis with citations.",
      insights: "Market opportunities, competitive landscape, strategic recommendations"
    }
  ];

  const integrationFeatures = [
    {
      icon: <Email style={{ fontSize: 32, color: '#4DC4D2' }} />,
      title: 'Email Intelligence',
      description: 'Maya specializes in email analysis and knowledge extraction',
      features: [
        'Microsoft Exchange & Gmail integration',
        'Thread analysis and relationship mapping',
        'Communication pattern insights',
        'Automatic contact relationship discovery'
      ]
    },
    {
      icon: <Description style={{ fontSize: 32, color: '#325BA8' }} />,
      title: 'Document Upload & Analysis',
      description: 'Upload and analyze documents directly in your knowledge graph',
      features: [
        'Support for PDF, Word, Excel, PowerPoint',
        'Automatic text extraction and OCR',
        'Instant semantic indexing',
        'Cross-document relationship discovery'
      ]
    },
    {
      icon: <Psychology style={{ fontSize: 32, color: '#E94257' }} />,
      title: 'AI-Powered Analysis',
      description: 'Sam provides intelligent document processing and insights',
      features: [
        'Automatic content extraction',
        'Semantic relationship discovery',
        'Key insight identification',
        'Cross-document pattern recognition'
      ]
    }
  ];

  const knowledgeFeatures = [
    {
      icon: <AccountTree style={{ fontSize: 40, color: '#4DC4D2' }} />,
      title: 'Semantic Knowledge Graph',
      description: 'Your information exists in a connected web of meaning, not isolated files.',
      benefits: [
        'Automatic relationship discovery',
        'Context-aware recommendations',
        'Cross-project insights',
        'Intelligent content surfacing'
      ]
    },
    {
      icon: <Search style={{ fontSize: 40, color: '#325BA8' }} />,
      title: 'Intelligent Search',
      description: 'Search by meaning, not just keywords. Find what you need even when you can\'t remember the exact words.',
      benefits: [
        'Natural language queries',
        'Concept-based search',
        'Intent understanding',
        'Multi-source results'
      ]
    },
    {
      icon: <AutoAwesome style={{ fontSize: 40, color: '#E94257' }} />,
      title: 'Context-Aware Insights',
      description: 'Sam surfaces relevant information and insights based on your current work context.',
      benefits: [
        'Proactive information delivery',
        'Work pattern recognition',
        'Deadline-aware prioritization',
        'Collaboration context sharing'
      ]
    },
    {
      icon: <Storage style={{ fontSize: 40, color: '#892987' }} />,
      title: 'Internet Search Integration',
      description: 'When internal knowledge isn\'t enough, SearXNG provides safe internet search with citations.',
      benefits: [
        'Safe internet search integration',
        'Automatic citation generation',
        'Dangerous site blocking',
        'Robots.txt compliance'
      ]
    }
  ];

  const useCases = [
    {
      title: 'Research Acceleration',
      problem: 'Spending hours searching for relevant information across emails, documents, and systems',
      solution: 'Sam instantly finds and synthesizes relevant information from all your sources',
      result: '80% faster research with better coverage and insights',
      example: 'Ask: "What did we learn from the last product launch?" Get comprehensive analysis from emails, documents, and meeting notes.'
    },
    {
      title: 'Institutional Knowledge Capture',
      problem: 'Valuable knowledge trapped in individual minds and scattered documents',
      solution: 'Knowledge graph automatically captures and connects expertise across the organization',
      result: '90% reduction in knowledge loss during transitions',
      example: 'When a team member leaves, their knowledge remains accessible and connected to ongoing projects.'
    },
    {
      title: 'Cross-Team Knowledge Sharing',
      problem: 'Teams reinventing solutions that already exist elsewhere in the organization',
      solution: 'Semantic search discovers relevant solutions and expertise across team boundaries',
      result: '60% reduction in duplicate work and improved solution quality',
      example: 'Engineering team discovers marketing\'s customer insights are directly relevant to their product feature.'
    },
    {
      title: 'Automated Insights Generation',
      problem: 'Important patterns and insights hidden in the volume of organizational data',
      solution: 'AI teammates continuously analyze and surface non-obvious connections and trends',
      result: '3x more strategic insights discovered and acted upon',
      example: 'System automatically identifies customer pain points mentioned across support emails, sales calls, and product feedback.'
    }
  ];

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Prismatic Forge Knowledge Management",
    "description": "AI-powered knowledge management system that transforms how organizations capture, organize, and discover insights from their information assets.",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "29",
      "priceCurrency": "USD",
      "priceSpecification": {
        "@type": "RecurringPriceSpecification",
        "billingFrequency": "monthly"
      }
    },
    "provider": {
      "@type": "Organization",
      "name": "Prismatic Forge"
    }
  };

  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      backgroundColor: '#fafafa',
      overflowX: 'hidden',
      overflowY: 'auto',
      paddingLeft: 16,
      paddingRight: 16,
    }}>
      <SEOHead
        title="AI Knowledge Management System - Intelligent Organizational Intelligence"
        description="Transform your organization's knowledge with AI-powered management. Automatically discover insights from emails, documents, and conversations. Try Sam, our document processing teammate."
        keywords="AI knowledge management, organizational intelligence, document analysis, email insights, knowledge graph, semantic search, enterprise knowledge base, AI knowledge discovery"
        ogTitle="AI Knowledge Management - Prismatic Forge"
        ogDescription="Discover hidden insights in your organizational knowledge with AI. Sam analyzes emails, documents, and conversations to surface connections you never knew existed."
        canonical="https://prismaticforge.ai/features/knowledge-management"
        jsonLd={structuredData}
      />
      {/* Hero Section */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0C1319 0%, #325BA8 100%)',
          color: 'white',
          paddingTop: 48,
          paddingBottom: 48,
          position: 'relative',
          overflow: 'hidden',
          marginLeft: -16,
          marginRight: -16,
        }}
      >
        <div style={{
          paddingLeft: 16,
          paddingRight: 16,
        }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                style={{
                  fontWeight: 'bold',
                  lineHeight: 1.1,
                  marginBottom: 24,
                }}
              >
                Transform Information Into{' '}
                <span style={{ color: '#4DC4D2' }}>
                  Intelligence
                </span>
              </Typography>
              <Typography
                variant="h5"
                style={{
                  marginBottom: 32,
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 300,
                  lineHeight: 1.4,
                }}
              >
                Your documents, emails, and data work together in a semantic knowledge graph.
                Sam (our document processing teammate) finds insights you never knew existed.
              </Typography>
              <Stack direction="row" spacing={2} style={{ marginBottom: 32 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/signup')}
                  style={{
                    backgroundColor: '#4DC4D2',
                    color: '#0C1319',
                    fontWeight: 'bold',
                    paddingLeft: 32,
                    paddingRight: 32,
                    paddingTop: 12,
                    paddingBottom: 12,
                  }}
                  endIcon={<ArrowForward />}
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  style={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'white',
                    paddingLeft: 32,
                    paddingRight: 32,
                    paddingTop: 12,
                    paddingBottom: 12,
                  }}
                >
                  Login
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                color: 'white',
              }}>
                <Typography variant="h5" style={{ marginBottom: 24, fontWeight: 'bold', color: '#4DC4D2' }}>
                  What You Get:
                </Typography>
                <List style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 8, paddingBottom: 8 }}>
                    <ListItemIcon style={{ minWidth: 40 }}>
                      <CheckCircle style={{ color: '#4DC4D2' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Semantic knowledge graph connects all your data"
                      primaryTypographyProps={{ fontSize: '1rem' }}
                    />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 8, paddingBottom: 8 }}>
                    <ListItemIcon style={{ minWidth: 40 }}>
                      <CheckCircle style={{ color: '#4DC4D2' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Automatic insight extraction from documents and emails"
                      primaryTypographyProps={{ fontSize: '1rem' }}
                    />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 8, paddingBottom: 8 }}>
                    <ListItemIcon style={{ minWidth: 40 }}>
                      <CheckCircle style={{ color: '#4DC4D2' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Discover hidden connections across your organization"
                      primaryTypographyProps={{ fontSize: '1rem' }}
                    />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 8, paddingBottom: 8 }}>
                    <ListItemIcon style={{ minWidth: 40 }}>
                      <CheckCircle style={{ color: '#4DC4D2' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="80% faster research with AI-powered synthesis"
                      primaryTypographyProps={{ fontSize: '1rem' }}
                    />
                  </ListItem>
                  <ListItem style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 8, paddingBottom: 8 }}>
                    <ListItemIcon style={{ minWidth: 40 }}>
                      <CheckCircle style={{ color: '#4DC4D2' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Enterprise-grade security and compliance"
                      primaryTypographyProps={{ fontSize: '1rem' }}
                    />
                  </ListItem>
                </List>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>

      {/* Core Features Section */}
      <div style={{ paddingTop: 80, paddingBottom: 80 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          style={{ marginBottom: 64, fontWeight: 'bold', color: '#325BA8' }}
        >
          Intelligence Features That Transform How You Work
        </Typography>
        <Grid container spacing={4}>
          {knowledgeFeatures.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                style={{
                  height: '100%',
                  padding: 24,
                  border: '1px solid rgba(76, 196, 210, 0.2)',
                  transition: 'all 0.3s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 16 }}>
                  {feature.icon}
                  <div style={{ marginLeft: 16, flex: 1 }}>
                    <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" style={{ color: 'rgba(0,0,0,0.6)', marginBottom: 24 }}>
                      {feature.description}
                    </Typography>
                    <List dense>
                      {feature.benefits.map((benefit, i) => (
                        <ListItem key={i} style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 4, paddingBottom: 4 }}>
                          <ListItemIcon style={{ minWidth: 32 }}>
                            <CheckCircle style={{ fontSize: 16, color: '#4DC4D2' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={benefit}
                            primaryTypographyProps={{ fontSize: '0.875rem' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </div>
                </div>
              </Card>
            </Grid>
          ))}
        </Grid>
      </div>

      {/* Sam in Action Section */}
      <div style={{ paddingTop: 64, paddingBottom: 64 }}>
        <div>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            style={{ marginBottom: 48, fontWeight: 'bold', color: '#325BA8' }}
          >
            Meet Sam: Your Document Processing Teammate
          </Typography>
          <Typography
            variant="h6"
            textAlign="center"
            style={{ marginBottom: 64, color: 'rgba(0,0,0,0.6)', maxWidth: 700, marginLeft: 'auto', marginRight: 'auto' }}
          >
            Sam doesn't just store your documents—Sam understands them, connects them, and extracts insights you never knew existed.
          </Typography>

          <Grid container spacing={4}>
            {samExamples.map((example, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  style={{
                    height: '100%',
                    padding: 24,
                    border: '1px solid rgba(76, 196, 210, 0.2)',
                  }}
                >
                  <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold', color: '#4DC4D2' }}>
                    {example.task}
                  </Typography>
                  <div style={{ marginBottom: 24 }}>
                    <Typography variant="body2" style={{ fontWeight: 'bold', color: 'rgba(0,0,0,0.6)', marginBottom: 8 }}>
                      Input:
                    </Typography>
                    <Typography variant="body2" style={{ fontStyle: 'italic', marginBottom: 16 }}>
                      {example.input}
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 'bold', color: 'rgba(0,0,0,0.6)', marginBottom: 8 }}>
                      Sam's Processing:
                    </Typography>
                    <Typography variant="body2" style={{ marginBottom: 16, fontSize: '0.875rem' }}>
                      {example.output}
                    </Typography>
                    <Typography variant="body2" style={{ fontWeight: 'bold', color: '#E94257', marginBottom: 8 }}>
                      Key Insights:
                    </Typography>
                    <Typography variant="body2" style={{ fontSize: '0.875rem', color: 'rgba(0,0,0,0.6)' }}>
                      {example.insights}
                    </Typography>
                  </div>
                </Card>
              </Grid>
            ))}
          </Grid>
        </div>
      </div>

      {/* Integration Showcase */}
      <div style={{ paddingTop: 80, paddingBottom: 80 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          style={{ marginBottom: 48, fontWeight: 'bold', color: '#325BA8' }}
        >
          Seamless Integration with Your Existing Tools
        </Typography>
        <Grid container spacing={4}>
          {integrationFeatures.map((integration, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                style={{
                  height: '100%',
                  padding: 24,
                  textAlign: 'center',
                  border: '1px solid rgba(76, 196, 210, 0.2)',
                  transition: 'all 0.3s ease',
                }}
              >
                <div style={{ marginBottom: 16 }}>
                  {integration.icon}
                </div>
                <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold' }}>
                  {integration.title}
                </Typography>
                <Typography variant="body1" style={{ color: 'rgba(0,0,0,0.6)', marginBottom: 24 }}>
                  {integration.description}
                </Typography>
                <List dense>
                  {integration.features.map((feature, i) => (
                    <ListItem key={i} style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 4, paddingBottom: 4, justifyContent: 'center' }}>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ fontSize: '0.875rem', textAlign: 'center' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Card>
            </Grid>
          ))}
        </Grid>
      </div>

      {/* Use Cases Section */}
      <div style={{ paddingTop: 64, paddingBottom: 64 }}>
        <div>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            style={{ marginBottom: 48, fontWeight: 'bold', color: '#325BA8' }}
          >
            Real-World Impact Stories
          </Typography>
          <Grid container spacing={3}>
            {useCases.map((useCase, index) => (
              <Grid item xs={12} key={index}>
                <Accordion
                  style={{
                    border: '1px solid rgba(76, 196, 210, 0.2)',
                    borderRadius: 8,
                    overflow: 'hidden',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    style={{
                      backgroundColor: 'rgba(76, 196, 210, 0.05)',
                    }}
                  >
                    <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                      {useCase.title}
                      <Chip
                        label={useCase.result}
                        size="small"
                        style={{
                          marginLeft: 16,
                          backgroundColor: '#4DC4D2',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      />
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#E94257', marginBottom: 8 }}>
                          The Problem:
                        </Typography>
                        <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)', marginBottom: 16 }}>
                          {useCase.problem}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#4DC4D2', marginBottom: 8 }}>
                          Our Solution:
                        </Typography>
                        <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)', marginBottom: 16 }}>
                          {useCase.solution}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#325BA8', marginBottom: 8 }}>
                          Example:
                        </Typography>
                        <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)', fontStyle: 'italic' }}>
                          {useCase.example}
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            ))}
          </Grid>
        </div>
      </div>

      {/* Performance & Security */}
      <div style={{ paddingTop: 80, paddingBottom: 80 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          style={{ marginBottom: 48, fontWeight: 'bold', color: '#325BA8' }}
        >
          Enterprise-Grade Performance & Security
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card style={{ padding: 32, height: '100%', backgroundColor: 'rgba(76, 196, 210, 0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                <Speed style={{ fontSize: 32, color: '#4DC4D2', marginRight: 16 }} />
                <Typography variant="h5" style={{ fontWeight: 'bold' }}>
                  Lightning-Fast Performance
                </Typography>
              </div>
              <List>
                {[
                  'Sub-100ms knowledge graph queries',
                  'Real-time document processing',
                  'Instant semantic search results',
                  'Scalable to millions of documents'
                ].map((feature, index) => (
                  <ListItem key={index} style={{ paddingLeft: 0 }}>
                    <ListItemIcon>
                      <CheckCircle style={{ color: '#4DC4D2' }} />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card style={{ padding: 32, height: '100%', backgroundColor: 'rgba(233, 66, 87, 0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
                <Security style={{ fontSize: 32, color: '#E94257', marginRight: 16 }} />
                <Typography variant="h5" style={{ fontWeight: 'bold' }}>
                  Security & Compliance
                </Typography>
              </div>
              <List>
                {[
                  'End-to-end encryption at rest and in transit',
                  'Granular access controls',
                  'Complete audit trail'
                ].map((feature, index) => (
                  <ListItem key={index} style={{ paddingLeft: 0 }}>
                    <ListItemIcon>
                      <CheckCircle style={{ color: '#E94257' }} />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </Card>
          </Grid>
        </Grid>
      </div>

      {/* CTA Section */}
      <div style={{ paddingTop: 80, paddingBottom: 80, textAlign: 'center' }}>
        <Typography
          variant="h3"
          component="h2"
          gutterBottom
          style={{ fontWeight: 'bold', marginBottom: 24 }}
        >
          Ready to Transform Your Knowledge Management?
        </Typography>
        <Typography
          variant="h6"
          style={{ marginBottom: 32, color: 'rgba(0,0,0,0.6)', fontWeight: 300, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}
        >
          Experience the power of connected knowledge. Let Sam show you insights you never knew existed in your own data.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signup')}
            style={{
              backgroundColor: '#4DC4D2',
              color: '#0C1319',
              fontWeight: 'bold',
              paddingLeft: 48,
              paddingRight: 48,
              paddingTop: 16,
              paddingBottom: 16,
              fontSize: '1.1rem',
            }}
            endIcon={<ArrowForward />}
          >
            Start Your Free Trial
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/chat')}
            style={{
              borderColor: '#4DC4D2',
              color: '#4DC4D2',
              fontWeight: 'bold',
              paddingLeft: 48,
              paddingRight: 48,
              paddingTop: 16,
              paddingBottom: 16,
              fontSize: '1.1rem',
            }}
          >
            Login
          </Button>
        </Stack>
        <Typography variant="body2" style={{ marginTop: 24, color: 'rgba(0,0,0,0.6)' }}>
          30-day free trial • No credit card required • Cancel anytime
        </Typography>
      </div>

      {/* FAQ Section */}
      <div style={{ paddingTop: 80, paddingBottom: 80 }}>
        <Typography
          variant="h3"
          component="h2"
          gutterBottom
          style={{ fontWeight: 'bold', marginBottom: 48, textAlign: 'center' }}
        >
          Frequently Asked Questions
        </Typography>
        <Typography
          variant="h6"
          style={{ marginBottom: 48, color: 'rgba(0,0,0,0.6)', textAlign: 'center', maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}
        >
          Everything you need to know about AI-powered knowledge management
        </Typography>

        <Grid container spacing={3}>
          {faqData.map((faq, index) => (
            <Grid item xs={12} key={index}>
              <Accordion
                style={{
                  backgroundColor: '#fff',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                >
                  <Typography variant="h6" style={{ fontWeight: 500 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ paddingTop: 0, paddingBottom: 24 }}>
                  <Typography variant="body1" style={{ color: 'rgba(0,0,0,0.6)', lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </Grid>
      </div>

      {/* Footer */}
      <div style={{ marginLeft: -16, marginRight: -16 }}>
        <Divider />
      </div>
      <div style={{ paddingTop: 32, paddingBottom: 32 }}>
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)' }}>
            © 2025 Prismatic Forge. All rights reserved.
          </Typography>
          <div style={{
            display: 'flex',
            gap: 24,
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}>
            <Typography
              component="a"
              href="/terms"
              variant="body2"
              style={{
                color: 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
              }}
            >
              Terms of Service
            </Typography>
            <Typography
              component="a"
              href="/privacy/"
              variant="body2"
              style={{
                color: 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              component="a"
              href="/docs"
              variant="body2"
              style={{
                color: 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
              }}
            >
              Documentation
            </Typography>
            <Typography
              component="a"
              href="mailto:support@prismaticforge.ai"
              variant="body2"
              style={{
                color: 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
              }}
            >
              Contact Support
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeManagementPage;
